import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setSelectedId, updateLinePosition } from "@/store/slideEditorSlice";
import { colors } from "@ai-teach/config";
import type { LineElement } from "@ai-teach/types";
import { useCallback, useEffect, useState } from "react";
import { LineRenderer } from "./LineRenderer";

interface DraggableLineProps {
  element: LineElement;
  isSelected?: boolean;
  scale?: number;
}

export function DraggableLine({ element, isSelected = false, scale = 1 }: DraggableLineProps) {
  const [dragging, setDragging] = useState<"start" | "end" | "line" | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isScreenshotMode, setIsScreenshotMode] = useState(false);

  const dispatch = useAppDispatch();
  const canvasSize = useAppSelector((state) => state.slideEditor.present.canvasSize);

  // Calculate canvas dimensions
  const canvasWidth = canvasSize.height * canvasSize.aspectRatio;
  const canvasHeight = canvasSize.height;

  // Helper function to clamp coordinates within canvas bounds
  const clampToCanvas = useCallback(
    (x: number, y: number) => ({
      x: Math.max(0, Math.min(x, canvasWidth - 5)),
      y: Math.max(0, Math.min(y, canvasHeight - 5)),
    }),
    [canvasWidth, canvasHeight]
  );

  const handleMouseDown = (e: React.MouseEvent, endpoint: "start" | "end" | "line") => {
    e.preventDefault();
    e.stopPropagation();

    // Select this line
    dispatch(setSelectedId(element.id));

    setDragging(endpoint);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Detect screenshot mode by checking if ancestor has screenshot-mode class
  useEffect(() => {
    const checkScreenshotMode = () => {
      const canvas = document.querySelector(".screenshot-mode");
      setIsScreenshotMode(!!canvas);
    };

    // Initial check
    checkScreenshotMode();

    // Watch for class changes on any element in the document
    const observer = new MutationObserver(checkScreenshotMode);

    // Observe the document body for any class changes in descendants
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = (e.clientX - dragStart.x) / scale;
      const deltaY = (e.clientY - dragStart.y) / scale;

      if (dragging === "start") {
        const newStart = clampToCanvas(element.startX + deltaX, element.startY + deltaY);
        dispatch(
          updateLinePosition({
            id: element.id,
            updates: {
              startX: newStart.x,
              startY: newStart.y,
            },
          })
        );
      } else if (dragging === "end") {
        const newEnd = clampToCanvas(element.endX + deltaX, element.endY + deltaY);
        dispatch(
          updateLinePosition({
            id: element.id,
            updates: {
              endX: newEnd.x,
              endY: newEnd.y,
            },
          })
        );
      } else if (dragging === "line") {
        const newStart = clampToCanvas(element.startX + deltaX, element.startY + deltaY);
        const newEnd = clampToCanvas(element.endX + deltaX, element.endY + deltaY);
        dispatch(
          updateLinePosition({
            id: element.id,
            updates: {
              startX: newStart.x,
              startY: newStart.y,
              endX: newEnd.x,
              endY: newEnd.y,
            },
          })
        );
      }

      setDragStart({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
      setDragging(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, dragStart, element, dispatch, scale, clampToCanvas]);

  // Calculate bounding box for the line (for ElementActionButtons positioning)
  const padding = 20; // Extra padding for stroke width and arrow markers
  const minX = Math.min(element.startX, element.endX) - padding;
  const minY = Math.min(element.startY, element.endY) - padding;
  const maxX = Math.max(element.startX, element.endX) + padding;
  const maxY = Math.max(element.startY, element.endY) + padding;

  const boundingBox = {
    left: Math.max(0, minX),
    top: Math.max(0, minY),
    width: maxX - minX,
    height: maxY - minY,
  };

  return (
    <>
      {/* Bounding box wrapper for ElementActionButtons positioning */}
      <div
        className="absolute"
        data-element-id={element.id}
        style={{
          left: `${boundingBox.left}px`,
          top: `${boundingBox.top}px`,
          width: `${boundingBox.width}px`,
          height: `${boundingBox.height}px`,
          pointerEvents: "none",
        }}
      />

      <div
        className={`absolute inset-0 ${isSelected ? "line-selected" : ""}`}
        style={{ pointerEvents: "none" }}
      >
        {/* Render the line */}
        <LineRenderer element={element} />

        {/* Draggable SVG overlay for interaction */}
        <svg
          className="absolute inset-0"
          style={{
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            overflow: "visible",
            pointerEvents: "none",
          }}
        >
          <title> </title>
          {/* Invisible thick line for easier grabbing */}
          <line
            x1={element.startX}
            y1={element.startY}
            x2={element.endX}
            y2={element.endY}
            stroke="transparent"
            strokeWidth={Math.max(element.strokeWidth || 2, 15)}
            style={{ cursor: dragging === "line" ? "grabbing" : "grab", pointerEvents: "auto" }}
            onMouseDown={(e) => handleMouseDown(e, "line")}
            onClick={handleClick}
          />

          {/* Show endpoint handles only when selected and not in screenshot mode */}
          {isSelected && !isScreenshotMode && (
            <>
              {/* Start point handle */}
              <circle
                cx={element.startX}
                cy={element.startY}
                r="6"
                fill={colors.primary[500]}
                stroke="white"
                strokeWidth="2"
                style={{ cursor: "move", pointerEvents: "auto" }}
                onMouseDown={(e) => handleMouseDown(e, "start")}
                onClick={handleClick}
                className="react-resizable-handle line-handle"
                data-screenshot-hide="true"
              />

              {/* End point handle */}
              <circle
                cx={element.endX}
                cy={element.endY}
                r="6"
                fill={colors.primary[500]}
                stroke="white"
                strokeWidth="2"
                style={{ cursor: "move", pointerEvents: "auto" }}
                onMouseDown={(e) => handleMouseDown(e, "end")}
                onClick={handleClick}
                className="react-resizable-handle line-handle"
                data-screenshot-hide="true"
              />
            </>
          )}
        </svg>
      </div>
    </>
  );
}
